import { Injectable } from '@angular/core';
import { IsProjectType } from '@project/model/is-project-type';
import { MENU_SEPARATOR, MenuItem, MenuList } from './menu.interfaces';
import { UserPermissionService } from '@user/service/user-permission.service';
import { UserUtils } from '@user/service/user-utils';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { ProjectModel } from '../../model/project.model';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { BackgroundTasksInfoService } from '../background-tasks-info.service';
import { UserModel } from '@user/model/user.model';
import { ProServeProjectImportManager } from '../../components/import-export/service/pro-serve-project-import.manager';
import { ProjectVO } from '../../model/vo/project.vo';
import { StringUtils } from '@shared/util/string-utils';
import { McsGdsDataService } from '../../components/article/service/mcs-gds-data.service';
import { ProjectChangedType } from '../../model/project-changed';
import { Subject } from 'rxjs/internal/Subject';
import { filter } from 'rxjs/operators';
import { getConfiguratorViewInfo } from '@configurator/view/configurator-view-info';
import { CsvImportService } from '../../csv/csv-import.service';

export type InterfaceMenuItemName =
  | 'importExportCae'
  | 'exportCad'
  | 'importCsv'
  | 'exportCsv'
  | 'comparePartsList'
  | 'importExportWsp'
  | 'importProServe'
  | 'printLabeling'
  | 'exportDataCenter'


@Injectable({providedIn: 'root'})
export class InterfaceMenuCreator {

  private isMcsGdsCadExportAvailable: boolean = false;

  private _menuChanged$ = new Subject<void>();
  public get menuChanged$() {
    return this._menuChanged$.asObservable();
  }

  constructor(
    private userPermissionService: UserPermissionService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectModel: ProjectModel,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
    private userModel: UserModel,
    private proServeProjectImportManager: ProServeProjectImportManager,
    private mcsGdsDataService: McsGdsDataService,
    private csvImportService: CsvImportService,
  ) {
    this.projectModel.projectChanged$
      .pipe(filter((change) => change.type === ProjectChangedType.ProjectTypeChanged))
      .subscribe(() => this.projectTypeChanged());
  }

  public create() {
    return {id: 'header-menu-interfaces', content: this.getInterfaceItems()};
  }

  public projectTypeChanged(): void {
    if (this.projectModel.currentProject && IsProjectType.mcsGds(this.projectModel.currentProject)) {
      const series: string | undefined = this.getMcsGdsProjectSeries(this.projectModel.currentProject);
      this.isMcsGdsCadExportAvailable = false;
      if (series) {
        this.mcsGdsDataService.checkCadExportAvailability(series).subscribe({
          next: (data) => this.setCadExportAvailability(data),
          error: (_error) => this.setCadExportAvailability(false),
        });
      }
    } else {
      this.isMcsGdsCadExportAvailable = true;
    }
  }

  private setCadExportAvailability(value: boolean): void {
    this.isMcsGdsCadExportAvailable = value;
    this._menuChanged$.next();
  }

  private getMcsGdsProjectSeries(project: ProjectVO): string | undefined {
    const articleNumber: string = project.infoProperties.getString("mcsGdsInitArticleNo");
    if (StringUtils.isEmpty(articleNumber) || articleNumber.indexOf('-') === -1) {
      return undefined;
    }
    return articleNumber.substring(0, articleNumber.indexOf('-'));
  }

  public createMenuItemPrintLabel(): MenuItem {
    return {
      label: 'header.menu.print_label',
      action: () => this.printLabeling(),
      enabled: true,
      visible: this.isPrintLabelingVisible(),
      appActionLogId: 'header.menu.print_label',
      id: 'header-menu-project-print-label',
    };
  }

  private getInterfaceItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.cae',
        action: () => this.actionWithCae(),
        enabled: this.userPermissionService.isCAEEnabled(),
        visible: this.userPermissionService.isCAEVisible(),
        appActionLogId: 'header.menu.cae',
        id: 'header-menu-import-cae',
      },
      {
        label: 'header.menu.cad_export',
        action: () => this.cadExport(),
        enabled: this.isCadExportEnabled(),
        visible: this.isCadExportVisible(),
        appActionLogId: 'header.menu.cad_export',
        id: 'header-menu-import-cad',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.csv',
        action: () => this.actionImportCsv(),
        enabled: this.userPermissionService.isLoggedIn(),
        visible: this.isImportCsvVisible(),
        appActionLogId: 'header.menu.csv',
      },
      {
        label: 'header.menu.generate_csv',
        action: () => this.generateCsvReport(),
        enabled: !this.backgroundTasksInfoService.isTaskActive("csv-export"),
        visible: this.isGenerateCsvVisible(),
        appActionLogId: 'header.menu.generate_csv',
        id: 'header-menu-export-csv',
      },
      {
        label: 'header.menu.compareItemList',
        action: () => this.compareItemLists(),
        enabled: this.isCompareItemListsEnabled(),
        visible: this.isCompareItemListsVisible(),
        appActionLogId: 'header.menu.compareItemList',
        id: 'header-menu-project-compare-item-list',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.local_backup',
        action: () => this.actionWithWsp(),
        enabled: true,
        visible: this.isActionWithWspVisible(),
        appActionLogId: 'header.menu.local_backup',
        id: 'header-menu-import-wsp',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.import_age',
        action: () => this.proServeProjectImportManager.importProServeProject(null),
        enabled: true,
        visible: this.isProServeImportVisible(),
        appActionLogId: 'header.menu.import_age',
        id: 'header-menu-import-pro-serve',
      },
      MENU_SEPARATOR,
      {
        label: 'header.menu.export_print_file',
        action: () => this.exportPrintFile(),
        enabled: true,
        visible: this.userPermissionService.isExportToWSIXVisible() || this.userPermissionService.isExportToWSCXVisible(),
        appActionLogId: 'header.menu.export_print_file',
      },
      MENU_SEPARATOR,
      this.createMenuItemPrintLabel(),
    ];
    return items;
  }

  private isActionWithWspVisible() {
    return getConfiguratorViewInfo(this.projectModel.currentProjectType).menu.interfaceMenuItemsVisibility['importExportWsp']
      && this.userPermissionService.isLocalBackupVisible();
  }

  private actionWithWsp(): void {
    this.projectComponentPresenter.openPopupActionWithWsp();
  }

  private isProServeImportVisible() {
    return this.hasInterfaceMenuItemVisibility('importProServe')
      && this.userPermissionService.isProServeImportVisible();
  }

  private isImportCsvVisible(): boolean {
    return this.hasInterfaceMenuItemVisibility('importCsv')
      && this.userPermissionService.isLoggedIn();
  }

  private actionWithCae(): void {
    this.projectComponentPresenter.openPopupActionWithCae();
  }

  private async cadExport(): Promise<void> {
    if (UserUtils.hasEditProjectPermission(this.projectModel.user) && this.projectModel.currentProjectRequiresSaving) {
      const saved = await this.projectComponentPresenter.openSaveProjectPopUp();
      if (saved) {
        this.executeCadExport();
      }
    } else {
      this.executeCadExport();
    }
  }

  private executeCadExport(): void {
    this.projectComponentPresenter.openCadExportPopup();
  }

  private isCadExportEnabled(): boolean {
    return !!this.projectModel.currentProjectType;
  }

  private isCadExportVisible(): boolean {
    return (
      this.hasInterfaceMenuItemVisibility('exportCad') &&
      this.isMcsGdsCadExportAvailable &&
      (!this.userPermissionService.isLoggedIn() || this.projectModel.user.hasPermission("EXPORT_TO_CAD"))
    );
  }

  private async actionImportCsv(): Promise<void> {
    await this.csvImportService.startCsvImport();
  }

  private generateCsvReport(): void {
    if (!ProjectUtils.isEmptyProject(this.projectModel.currentProject)) {
      this.projectComponentPresenter.openSelectProjectForCSVPopup();
    } else {
      this.projectNavigationPresenter.openSaveCsvDialog();
    }
  }

  private isGenerateCsvVisible(): boolean {
    if (IsProjectType.markingSystem(this.projectModel.currentProject)) return false;
    return this.hasInterfaceMenuItemVisibility('exportCsv')
      && (!this.userPermissionService.isLoggedIn() || this.userModel.checkPermissions(["GENERATE_REPORTS", "GENERATE_PART_LIST"]));
  }

  private compareItemLists(): void {
    this.projectNavigationPresenter.openCompareItemListDialog();
  }

  private isCompareItemListsEnabled(): boolean {
    return this.isCompareItemListsVisible();
  }

  private isCompareItemListsVisible(): boolean {
    return this.hasInterfaceMenuItemVisibility('comparePartsList')
      && ProjectUtils.isProjectItemListCompareAvailable(
        this.projectModel.currentProject,
        this.userModel.user,
        this.userModel.isConstruction);
  }

  private exportPrintFile(): void {
    this.projectComponentPresenter.openPopupActionWithExportPrint();
  }

  private printLabeling(): void {
    this.projectComponentPresenter.openPrintLabelingPopup();
  }

  private isPrintLabelingVisible() {
    return this.hasInterfaceMenuItemVisibility('printLabeling')
      && this.userModel.checkPermissions(["EXPORT_LABELING_TO_SMART_SCRIPT"]);
  }

  private hasInterfaceMenuItemVisibility(InterfaceMenuItemName: InterfaceMenuItemName): boolean {
    return getConfiguratorViewInfo(this.projectModel.currentProjectType).menu.interfaceMenuItemsVisibility[InterfaceMenuItemName];
  }
}
